/**
 * AlertsDashboard.tsx — Alert Router dashboard (/dashboard/alerts)
 *
 * Purpose:    Manage routing policies (condition builder + escalation chain),
 *             on-call schedule (weekly calendar view), and silence manager.
 * Inputs:     useAlertRouterData hook (port 3834)
 * Outputs:    PolicyList + PolicyEditor + OnCallSchedule + SilenceManager
 * Constraints: 7-state AsyncScreen; no external calendar dep
 * SOT:        T-P3-E5-W2-S4-T01
 */

import { useState } from 'react'
import { AsyncScreen } from '../../components/AsyncScreen'
import {
  useAlertRouterData,
  type AlertPolicy,
} from '../../hooks/useAlertRouterData'
import { OnCallSchedule } from './OnCallSchedule'
import { PolicyEditor } from './PolicyEditor'
import { SilenceManager } from './SilenceManager'

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function AlertsSkeleton() {
  return (
    <div className="animate-pulse space-y-3 p-6" aria-hidden>
      {[...Array(3)].map((_, i) => (
        <div key={i} className="h-12 rounded-md bg-brand-surface-2" />
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AlertsDashboard() {
  const { state, data, error, refresh, savePolicy, deletePolicy, createSilence, deleteSilence } =
    useAlertRouterData()
  const [editingPolicy, setEditingPolicy] = useState<AlertPolicy | 'new' | null>(null)

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-brand-ink">Alert Router</h1>
          <p className="mt-1 text-sm text-brand-muted">
            Routing policies, on-call schedule, and silences
          </p>
        </div>
        <button
          type="button"
          onClick={() => { void refresh() }}
          className="rounded-md border border-brand-card px-3 py-1.5 text-sm text-brand-secondary hover:bg-brand-surface-2"
        >
          Refresh
        </button>
      </div>

      <AsyncScreen
        state={state}
        error={error}
        onRetry={() => { void refresh() }}
        loadingSkeleton={<AlertsSkeleton />}
        emptyMessage="No routing policies configured"
      >
        {data && (
          <div className="space-y-8">
            {/* Policies */}
            <section>
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-brand-secondary">
                  Routing policies
                </h2>
                <button
                  type="button"
                  onClick={() => setEditingPolicy('new')}
                  className="text-xs text-sky-700 dark:text-sky-400 hover:underline"
                >
                  + New policy
                </button>
              </div>
              {editingPolicy === 'new' && (
                <div className="mb-3">
                  <PolicyEditor onSave={savePolicy} onCancel={() => setEditingPolicy(null)} />
                </div>
              )}
              <div className="space-y-2">
                {data.policies.map((p) =>
                  editingPolicy !== 'new' && editingPolicy?.id === p.id ? (
                    <PolicyEditor
                      key={p.id}
                      initial={p}
                      onSave={savePolicy}
                      onCancel={() => setEditingPolicy(null)}
                    />
                  ) : (
                    <div
                      key={p.id}
                      className="flex items-center justify-between rounded-md border border-brand-subtle bg-brand-surface/50 px-4 py-3"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className={`h-2 w-2 rounded-full ${p.enabled ? 'bg-green-500' : 'bg-gray-600'}`}
                        />
                        <span className="font-medium text-brand-ink">{p.name}</span>
                        <span className="text-xs text-brand-muted">
                          {p.conditions.length} condition{p.conditions.length !== 1 ? 's' : ''} ·{' '}
                          {p.escalationChain.length} step{p.escalationChain.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setEditingPolicy(p)}
                          className="text-xs text-sky-700 dark:text-sky-400 hover:underline"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => { void deletePolicy(p.id) }}
                          className="text-xs text-red-700 dark:text-red-400 hover:underline"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </section>

            {/* On-call schedule */}
            <section>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-brand-secondary">
                On-call schedule
              </h2>
              <OnCallSchedule slots={data.onCallSchedule} />
            </section>

            {/* Silences */}
            <section>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-brand-secondary">
                Silences
              </h2>
              <SilenceManager
                silences={data.silences}
                onCreate={createSilence}
                onDelete={deleteSilence}
              />
            </section>
          </div>
        )}
      </AsyncScreen>
    </div>
  )
}
